/**
 * Client-side apply of a saved column mapping to sheet info from an inferred load.
 * Mirrors backend `apply_saved_mapping_to_sheet` / `apply_saved_mapping_to_sheet_info`.
 */

import { synchronizeQuestionMappingsByLabel } from "./field-mappings";

export type SavedImportMappingWarning = {
  warning_type: string;
  field_key: string;
  mapped_value: string;
  message: string;
};

export type SheetInfoEntry = {
  headers?: unknown[];
  field_mapping?: Record<string, string>;
  question_labels?: Record<string, string>;
  mapping_warnings?: SavedImportMappingWarning[];
  [key: string]: unknown;
};

/**
 * Apply saved field mappings to each sheet without re-fetching the spreadsheet.
 * `sheetInfo` must be the baseline from an inference pass (same shape as API response).
 */
export function applySavedFieldMappingToSheetInfo(
  sheetInfo: Record<string, SheetInfoEntry>,
  savedFieldMappings: Record<string, string>,
): Record<string, SheetInfoEntry> {
  const result: Record<string, SheetInfoEntry> = {};

  for (const [sheetName, info] of Object.entries(sheetInfo)) {
    const headersRaw = info.headers;
    const headers = Array.isArray(headersRaw) ? headersRaw : [];
    const normalizedHeaders: Record<string, string> = {};
    for (const h of headers) {
      if (h == null) continue;
      const s = String(h).trim();
      if (!s) continue;
      normalizedHeaders[s] = String(h);
    }

    const supportedKeys = new Set(Object.keys(info.field_mapping || {}));
    const baseline = { ...(info.field_mapping || {}) } as Record<
      string,
      string
    >;
    const applied: Record<string, string> = {};
    const warnings: SavedImportMappingWarning[] = [];

    for (const [fieldKey, mappedValue] of Object.entries(savedFieldMappings)) {
      if (!fieldKey) continue;
      if (!supportedKeys.has(fieldKey)) {
        warnings.push({
          warning_type: "stale_field",
          field_key: fieldKey,
          mapped_value: mappedValue,
          message: `Saved mapping field '${fieldKey}' is not available for this upload and was skipped.`,
        });
        continue;
      }
      const trimmed = String(mappedValue ?? "").trim();
      if (!trimmed) continue;
      if (trimmed.startsWith("FIXED:")) {
        applied[fieldKey] = trimmed;
        continue;
      }
      const matchedHeader = normalizedHeaders[trimmed];
      if (matchedHeader === undefined) {
        warnings.push({
          warning_type: "missing_column",
          field_key: fieldKey,
          mapped_value: mappedValue,
          message: `Saved column '${mappedValue}' for '${fieldKey}' was not found in this sheet.`,
        });
        continue;
      }
      applied[fieldKey] = matchedHeader;
    }

    const merged: Record<string, string> = { ...baseline };
    for (const [k, v] of Object.entries(applied)) {
      merged[k] = v;
    }
    for (const fieldKey of supportedKeys) {
      if (fieldKey.startsWith("question_") && !(fieldKey in applied)) {
        merged[fieldKey] = "";
      }
    }

    result[sheetName] = {
      ...info,
      field_mapping: synchronizeQuestionMappingsByLabel(
        merged,
        info.question_labels,
      ),
      mapping_warnings: warnings,
    };
  }

  return result;
}
